import React, { useEffect, useState } from 'react'
import { Helmet } from 'react-helmet'
import { useSelector } from 'react-redux'
import HowToJoinPage from 'src/app/Signup/HowToJoinPage'
import SignupFullPage from 'src/app/Signup/SignupFullPage'
import SignupHalfPage from 'src/app/Signup/SignupHalfPage'
import MobileWebPopup from 'src/components/MobileWebPopup/MobileWebPopup'
import { useUser } from 'src/context/UserProvider/useUser'
import { SupportedBrands } from 'src/enums'
import SignupCMS from 'src/feature/MarketingCMS/SignupCMS'
import { selectIsMobileWebPopupOpenFromState } from 'src/redux/selectors/app.selectors'
import { selectFeatureFlaggerFromState } from 'src/redux/selectors/data/featureFlagger.selectors'
import { selectQueryParams } from 'src/redux/selectors/router.selectors'
import {
  selectAppName,
  selectBrandName,
  selectRemoteConfigFromState,
} from 'src/redux/selectors/session.selectors'
import { isSSOLoginEnabled } from 'src/utils/featureFlags'

import style, { mobilePopupStyle } from './SignupPage.style'

const SignupPage: React.FC = () => {
  const brand = useSelector(selectBrandName)

  const appName = useSelector(selectAppName)
  const queryParams = useSelector(selectQueryParams)
  const isMobileWebPopupOpen = useSelector(selectIsMobileWebPopupOpenFromState)
  const featureFlagger = useSelector(selectFeatureFlaggerFromState)

  const remoteConfig = useSelector(selectRemoteConfigFromState)
  const remoteConfigLoaded = remoteConfig.loadedConfig === true

  const userContext = useUser()

  const { orgName, inviteeEmail, inviterName, inviterEmail } = queryParams

  const organizationId = new URLSearchParams(window.location.search).get(
    'organizationId',
  )

  const signupAndJoin = !!(
    orgName &&
    inviteeEmail &&
    inviterName &&
    inviterEmail
  )

  const ssoEnabled = isSSOLoginEnabled(
    featureFlagger,
    organizationId ?? undefined,
  )

  const [showHowToJoin, setShowHowToJoin] = useState<boolean>(false)
  const [authConfigLoading, setAuthConfigLoading] =
    useState<boolean>(!!inviteeEmail)
  const [isSsoOrg, setIsSsoOrg] = useState<boolean>(false)

  useEffect(() => {
    if (!inviteeEmail) return
    if (!remoteConfigLoaded) return
    if (!ssoEnabled) {
      setIsSsoOrg(false)
      setAuthConfigLoading(false)
      return
    }

    setAuthConfigLoading(true)
    userContext
      .getAuthConfigFromEmail(inviteeEmail)
      .then((result) => {
        setIsSsoOrg(result.authType === 'sso' && !!result.identityProviderName)
        setAuthConfigLoading(false)
      })
      .catch(() => {
        // On error, fall back to normal signup flow
        setIsSsoOrg(false)
        setAuthConfigLoading(false)
      })
  }, [inviteeEmail, ssoEnabled, remoteConfigLoaded])

  let content
  if (showHowToJoin) {
    content = <HowToJoinPage setShowHowToJoin={setShowHowToJoin} />
  } else if (brand === SupportedBrands.Extend) {
    content = (
      <div css={style}>
        <SignupCMS />
        <SignupHalfPage
          setShowHowToJoin={setShowHowToJoin}
          authConfigLoading={authConfigLoading}
          isSsoOrg={isSsoOrg}
        />
      </div>
    )
  } else {
    content = (
      <SignupFullPage
        setShowHowToJoin={setShowHowToJoin}
        authConfigLoading={authConfigLoading}
        isSsoOrg={isSsoOrg}
      />
    )
  }

  return (
    <>
      <Helmet>
        <title>{`Get started today and create virtual cards with the card in your pocket. | ${appName}`}</title>
        <meta
          name="description"
          content={`Create an ${appName} account to create, send, and manage virtual cards for all your business payments.`}
        />
      </Helmet>
      {isMobileWebPopupOpen && (
        <div css={mobilePopupStyle}>
          <MobileWebPopup signupAndJoin={signupAndJoin} />
        </div>
      )}
      {content}
    </>
  )
}

export default SignupPage
